import React from 'react';
import { Typography, Box, Divider } from '@mui/material';
import { useNavigateWithScrollMemory } from '../../../hooks/useNavigateWithScrollMemory';
import { Link as RouterLink } from 'react-router-dom';
import AlbumCover from '../../../components/common/AlbumCover';
import theme from '../../../theme';
import RatingReadOnly from '../../../components/common/RatingReadOnly';
import LikeCount from '../../../components/common/LikeCount';
import CommentCount from '../../../components/common/CommentCount';
import ArtistLink from '../../../components/common/ArtistLink';
import AlbumTitleLink from '../../../components/common/AlbumTitleLink';

const MyReviewVerticalCard = ({ data, referrerType }) => {
  const navigateWithScrollMemory = useNavigateWithScrollMemory();
  const albumGenres =
    data.album?.genres ||
    data.album?.genreNames ||
    data.album?.albumGenres ||
    data.genreNames ||
    data.albumGenres ||
    [];
  const albumUrl = data.album.slug
    ? `/reviews/${data.album.slug}`
    : `/reviews/${data.album.albumSource?.toLowerCase()}/${data.album.sourceId}`;
  const reviewUrl = `${albumUrl}/user-reviews/${data.review.id}`;

  const navState = referrerType ? { state: { referrerType } } : undefined;

  const handleNavigate = (e, url) => {
    if (!e.metaKey && !e.ctrlKey) {
      e.preventDefault();
      navigateWithScrollMemory(url, navState);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        gap: { md: 1.5, lg: 2 },
      }}
    >
      {/* 이미지 */}
      <Box
        component={RouterLink}
        to={albumUrl}
        state={navState?.state}
        onClick={(e) => handleNavigate(e, albumUrl)}
        sx={{ display: 'block', width: '100%', flexShrink: 0 }}
      >
        <AlbumCover
          imageUrl={data.album.imageUrl}
          altText={data.album.title}
          isStarred={data.album.isStarred}
          sx={{
            aspectRatio: '1 / 1',
            width: '100%',
            cursor: 'pointer',
            objectFit: 'cover',
            display: 'block',
          }}
        />
      </Box>

      {/* 정보 */}
      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
        <AlbumTitleLink
          title={data.album.title}
          to={albumUrl}
          contentRating={data.album.contentRating}
          sx={{ fontSize: { md: 14, lg: 16 } }}
          iconSx={{ width: { md: '14px', lg: '16px' }, height: { md: '14px', lg: '16px' } }}
        />
        <ArtistLink
          artistName={data.artist?.name}
          artistSource={data.artist?.artistSource}
          artistSourceId={data.artist?.sourceId}
          artistSlug={data.artist?.slug}
          artistDbId={data.artist?.id}
          sx={{ fontSize: { md: 13, lg: 14 }, color: theme.palette.black[4], mt: 1 }}
        />

        {albumGenres.length > 0 && (
          <Typography
            fontSize={{ md: 13, lg: 14 }}
            sx={{
              color: theme.palette.black[4],
              mt: 1,
              display: '-webkit-box',
              WebkitLineClamp: 1,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              wordBreak: 'break-word',
              lineHeight: 1.4,
            }}
            title={albumGenres.slice(0, 3).join(', ')}
          >
            {albumGenres.slice(0, 3).join(', ')}
          </Typography>
        )}

        <Box sx={{ mt: { md: 1.5, lg: 2 } }}>
          <RatingReadOnly value={data.review.rating} variant="iconfix" />
        </Box>

        {/* 리뷰 내용 */}
        <Typography
          component={RouterLink}
          to={reviewUrl}
          state={navState?.state}
          onClick={(e) => handleNavigate(e, reviewUrl)}
          fontSize={{ md: 13, lg: 14 }}
          color={theme.palette.black[6]}
          sx={{
            textDecoration: 'none',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            wordBreak: 'break-word',
            lineHeight: '1.5',
            mt: { md: 2, lg: 3.75 },
          }}
        >
          {data.review.content}
        </Typography>

        {/* 날짜 | 좋아요 수, 댓글 수 */}
        <Box display="flex" gap={1} alignItems="center" sx={{ mt: 2 }}>
          <Typography
            fontSize={{ md: 13, lg: 14 }}
            sx={{ color: theme.palette.black[4], whiteSpace: 'nowrap' }}
          >
            {data.review.createdAt}
          </Typography>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              height: '8px',
              alignSelf: 'center',
              backgroundColor: theme.palette.grey.line,
            }}
          />
          <Box display="flex" gap={1.5} alignItems="center">
            <LikeCount likeCount={data.review.likeCount} />
            <CommentCount commentCount={data.review.commentCount} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MyReviewVerticalCard;
